import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Button, { ButtonProps, ButtonTypeMap } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import * as React from 'react';

type Props = ButtonProps & {
    isLoading: boolean;
    isSuccess?: boolean;
    isError?: boolean;
    successLabel?: string;
    errorLabel?: string;
};

/**
 * ButtonLoading
 *
 * @description
 * A wrapper component for Mui-Button; Added behavior includes becoming disabled and displaying a
 * loading spinner when the showLoading prop is truthy.
 */
export default function ButtonLoading({
    isLoading,
    isSuccess = false,
    isError = false,
    successLabel = 'Saved',
    errorLabel = 'Error',
    children,
    disabled,
    ...props
}: Props) {
    const [buttonState, setButtonState] = React.useState<{
        label: string | React.ReactNode;
        icon: React.ReactNode | undefined;
        disabled: boolean;
        color: ButtonTypeMap['props']['color'] | undefined;
    }>({
        label: children,
        icon: props.startIcon,
        disabled: false,
        color: props.color,
    });

    React.useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (isLoading) {
            setButtonState({
                label: 'Loading...',
                icon: <CircularProgress size={14} />,
                disabled: true,
                color: props.color,
            });
        } else if (isSuccess) {
            setButtonState({
                label: successLabel,
                icon: <CheckIcon />,
                disabled: false,
                color: 'success',
            });
            timeout = setTimeout(
                () =>
                    setButtonState({
                        label: children,
                        icon: props.startIcon,
                        disabled: false,
                        color: props.color,
                    }),
                5000,
            );
        } else if (isError) {
            setButtonState({
                label: errorLabel,
                icon: <CloseIcon />,
                disabled: false,
                color: 'error',
            });
            timeout = setTimeout(
                () =>
                    setButtonState({
                        label: children,
                        icon: props.startIcon,
                        disabled: false,
                        color: props.color,
                    }),
                5000,
            );
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [
        isLoading,
        isSuccess,
        isError,
        successLabel,
        errorLabel,
        children,
        props.startIcon,
        props.color,
    ]);

    return (
        <Button
            {...props}
            disabled={disabled || isLoading}
            startIcon={buttonState.icon}
            color={buttonState.color}
        >
            {buttonState.label}
        </Button>
    );
}
